export interface Subscription {
  planName: SubscriptionPlanName;
  status: SubscriptionStatus;
  url: string;
  id: string;
  billingUrl: string;
  billingDate: string;
  secondsUsed: number;
  minutesIncluded: number;
  totalNumCallsBillingPeriod: number;
  trialMinutes: number;
  externalSubscriptionId?: string;
  externalPspCustomerId?: string;
  ownerEmail?: string;
  overageCost: number;
  features: Feature[];
  planCost: number;
  isCreditCardAdded: boolean;
  upcomingPlanName: SubscriptionPlanName;
  isCanceled: boolean;
  isPaused: boolean;
  pausedAt: string | null;
  cardType?: string;
  cardLastFour?: string;
  cardExpDate?: string;
  trialEndsAt?: string;
  trialDaysLeft?: number;
  trialDays?: number;

  // V4 Website Texting fields
  websiteTextingEnabled?: boolean;
  websiteTextingSubscribedAt?: string | null;
  websiteTextingDiscountId?: string | null;
  websiteTextingUsage?: WebsiteTextingUsage;

  // Provider billing fields
  billingProvider?: BillingProvider;
  providerStatus?: string;
  providerConfirmationUrl?: string;
}

export interface SubscriptionData {
  paymentMethod: string;
  prices: SubscriptionPrice[];
  couponCode?: string;
}

export enum SubscriptionStatus {
  Incomplete = 'incomplete',
  IncompleteExpired = 'incomplete_expired',
  Trialing = 'trialing',
  Active = 'active',
  PastDue = 'past_due',
  Canceled = 'canceled',
  Unpaid = 'unpaid',
  Paused = 'paused',
  PendingApproval = 'pending_approval',
  Expired = 'expired'
}

export interface SubscriptionPrice {
  id: string;
  currency: string;
  amount: number;
  interval: string;
  usageType: SubscriptionType;
}

export interface SubscriptionPlan {
  id: string;
  name: SubscriptionPlanName;
  description: string | null;
  minutesIncluded: string;
  prices: SubscriptionPrice[];
  features?: string[];
  // Shopify-only display copy from the plans API (null for other providers).
  highlight?: string | null;
  inheritsLabel?: string | null;
  couponPreview?: CouponPreview;
}

export interface CouponPreview {
  coupon: Coupon;
  prices: CouponPreviewPrice[];
}

export interface CouponPreviewPrice {
  priceId: string;
  interval: string;
  originalAmount: number;
  discountedAmount: number;
  savings: number;
}

export enum SubscriptionPlanName {
  Trial = 'Trial',
  Professional = 'Professional',
  Scale = 'Scale',
  Growth = 'Growth',
  None = 'Canceled'
}

export enum SubscriptionType {
  Licensed = 'licensed'
}

export enum SubscriptionInterval {
  Monthly = 'month',
  Yearly = 'year'
}

export enum ProviderBillingInterval {
  Every30Days = 'EVERY_30_DAYS',
  Annual = 'ANNUAL',
  /** Apple IAP uses lowercase interval value 'month' */
  Monthly = 'month',
}

/** @deprecated Use ProviderBillingInterval */
export const ShopifyBillingInterval = ProviderBillingInterval;
export type ShopifyBillingInterval = ProviderBillingInterval;

export interface ProviderPlan {
  name: string;
  amount: number;
  interval: ProviderBillingInterval;
  minutesIncluded: number;
  key: string;
  isCustom?: boolean;
}

export enum Feature {
  Appointments = 'appointment_links',
  AppointmentScheduling = 'appointment_scheduling',
  TransferCalls = 'call_transfers',
  TrainingFiles = 'training_files',
  TextLinks = 'text_a_link',
  SmsConversations = 'sms_conversations',
  MessageScenarios = 'message_scenarios',
  WaterfallTransfers = 'waterfall_call_transfers',
}

export interface InvoicePreview {
  proration: string | null;
  subtotal: string;
  total: string;
}

export interface PlanDetails {
  planName: string;
  originalPlanCost: number;
  discountedPlanCost: number;
  planFrequency: SubscriptionInterval;
  couponsApplied: Coupon[];
  billingCycleStart: string;
  billingCycleEnd: string;
}

export interface Coupon {
  id: string;
  name: string;
  amountOff: number | null;
  percentOff: number | null;
  duration: 'forever' | 'once' | 'repeating';
  durationInMonths: number | null;
}

export interface SubscriptionDetails {
  currentPlan: PlanDetails | null;
  upcomingPlan: PlanDetails | null;
}

export enum WebsiteTextingStatus {
  Disabled = 'disabled',
  Enabled = 'enabled',
  SetToCancel = 'set_to_cancel'
}

export interface WebsiteTextingSubscription {
  status: WebsiteTextingStatus;
  price: number;
  conversationsIncluded: number;
  overageRate: number;
  nextBillingDate: string | null;
  cancellationDate: string | null;
  discountedPrice: number | null;
  couponsApplied: Coupon[];
}

export interface WebsiteTextingUsage {
  includedConversations: number;
  usedConversations: number;
  overageConversations: number;
  overageRate: number;
  billingResetDate: string;
}

export enum BillingProvider {
  Stripe = 'stripe',
  Apple = 'apple',
  Shopify = 'shopify',
}